import { Box, HStack, Icon, IconButton, Spinner, Text } from '@chakra-ui/react'
import type { Meta, Body, UppyFile } from '@uppy/core'
import truncate from 'lodash/truncate'
import type { FC } from 'react'
import { useCallback } from 'react'
import { PiFile, PiFileDoc, PiFileHtml, PiFileJs, PiFileMd, PiFilePdf, PiFilePy, PiX } from 'react-icons/pi'

import { Tooltip } from '@app/components/ui/tooltip'

interface Props<M extends Meta = Meta, B extends Body = Body> {
  file: UppyFile<M, B>
  onDelete?: (file: UppyFile<M, B>) => void
}

const File: FC<Props> = ({ file, onDelete = null }) => {
  let IconEl = PiFile
  const { percentage } = file.progress

  if (file.extension === 'pdf') {
    IconEl = PiFilePdf
  } else if (file.extension === 'doc' || file.extension === 'docx') {
    IconEl = PiFileDoc
  } else if (file.extension === 'html') {
    IconEl = PiFileHtml
  } else if (file.extension === 'js' || file.extension === 'json' || file.extension === 'ts') {
    IconEl = PiFileJs
  } else if (file.extension === 'md') {
    IconEl = PiFileMd
  } else if (file.extension === 'py') {
    IconEl = PiFilePy
  }

  const onClick = useCallback(
    (e) => {
      if (onDelete) {
        e.stopPropagation()
        onDelete(file)
      }
    },
    [onDelete, file]
  )

  return (
    <HStack key={file.toString()} gap={0} border="border" borderWidth={1} borderRadius="sm" bgColor="bg">
      <Box h="100%" px={2} py={1} textAlign="center" borderRadius="sm" borderRightRadius={0} bgColor="fg.subtle">
        {percentage > 0 && percentage < 100 ? (
          <Spinner boxSize={4} color="fg.panel" />
        ) : (
          <Icon boxSize={4} color="fg.panel">
            <IconEl />
          </Icon>
        )}
      </Box>
      <Text w="100%" px={2} color="fg.subtle" fontSize="xs" title={file.name}>
        {truncate(file.name, { length: 20 })}
      </Text>
      {onDelete && (
        <Tooltip content="Remove this file">
          <Box>
            <IconButton size="xs" aria-label="Remove file" onClick={onClick} variant="ghost">
              <PiX />
            </IconButton>
          </Box>
        </Tooltip>
      )}
    </HStack>
  )
}

export default File
